import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, Flask, Plus, Play, Stop, Trash, ChartBar, X as XIcon,
  CheckCircle, Clock, TrendUp, Copy, PencilSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";

const STATUS_META = {
  draft:   { label: "Draft",   cls: "text-neutral-400 border-white/15 bg-white/[0.03]" },
  running: { label: "Running", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  stopped: { label: "Stopped", cls: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
};

function StatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
}

const EMPTY_FORM = {
  key: "",
  name: "",
  description: "",
  slot_key: "",
  page_path: "/lp/small-fleet",
  status: "draft",
  variants: [
    { id: "control", name: "Control", weight: 50, content: "" },
    { id: "b", name: "Variant B", weight: 50, content: "" },
  ],
};

function ExperimentForm({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        key: initial.key || "",
        name: initial.name || "",
        description: initial.description || "",
        slot_key: initial.slot_key || "",
        page_path: initial.page_path || "/lp/small-fleet",
        status: initial.status || "draft",
        variants: (initial.variants || []).map((v) => ({ ...v })),
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, initial]);

  if (!open) return null;

  const setVariant = (idx, field, value) => {
    setForm((f) => {
      const variants = f.variants.map((v, i) => (i === idx ? { ...v, [field]: value } : v));
      return { ...f, variants };
    });
  };

  const addVariant = () => {
    const nextId = String.fromCharCode(99 + form.variants.length - 1); // c, d, e...
    setForm((f) => ({
      ...f,
      variants: [...f.variants, { id: nextId, name: `Variant ${nextId.toUpperCase()}`, weight: 25, content: "" }],
    }));
  };

  const removeVariant = (idx) => {
    if (form.variants.length <= 2) return;
    setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    // client-side sanity
    if (!form.key || !form.name || !form.slot_key || !form.page_path) {
      toast.error("Key, name, slot key, and page path are required");
      return;
    }
    if (form.variants.some((v) => !v.id || !v.name || !v.content)) {
      toast.error("Each variant needs an id, name, and content");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const patch = {
          name: form.name,
          description: form.description,
          slot_key: form.slot_key,
          page_path: form.page_path,
          variants: form.variants.map((v) => ({
            id: v.id, name: v.name, weight: Number(v.weight) || 50, content: v.content,
          })),
          status: form.status,
        };
        await client.patch(`/admin/experiments/${initial.id}`, patch);
        toast.success("Experiment updated");
      } else {
        await client.post("/admin/experiments", {
          ...form,
          variants: form.variants.map((v) => ({
            id: v.id, name: v.name, weight: Number(v.weight) || 50, content: v.content,
          })),
        });
        toast.success("Experiment created");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="experiment-form-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-3xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                <Flask size={12} weight="fill" /> {isEdit ? "Edit experiment" : "New experiment"}
              </div>
              <div className="text-lg font-semibold text-white">{form.name || "Untitled experiment"}</div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white"
                    data-testid="experiment-modal-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Key (unique)</div>
                <input
                  type="text"
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_\-]/g, "_") })}
                  disabled={isEdit}
                  placeholder="lp_smallfleet_hero_headline_v1"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono disabled:opacity-50"
                  data-testid="experiment-key-input"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Status</div>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="experiment-status-select"
                >
                  <option value="draft">Draft (hidden)</option>
                  <option value="running">Running (live to visitors)</option>
                  <option value="stopped">Stopped (frozen)</option>
                </select>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Name</div>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Hero headline A/B (small-fleet ad)"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="experiment-name-input"
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Description (optional)</div>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What are we testing and why?"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Page path</div>
                <input
                  type="text"
                  value={form.page_path}
                  onChange={(e) => setForm({ ...form, page_path: e.target.value })}
                  placeholder="/lp/small-fleet"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
                  data-testid="experiment-path-input"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Slot key (matches frontend getSlot key)</div>
                <input
                  type="text"
                  value={form.slot_key}
                  onChange={(e) => setForm({ ...form, slot_key: e.target.value.toLowerCase().replace(/[^a-z0-9_\-]/g, "_") })}
                  placeholder="hero_headline"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
                  data-testid="experiment-slot-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Variants</div>
                <button
                  type="button"
                  onClick={addVariant}
                  disabled={form.variants.length >= 6}
                  data-testid="experiment-add-variant-btn"
                  className="text-xs px-3 py-1 rounded-full border border-white/15 text-neutral-300 hover:text-white hover:border-white/40 transition-colors inline-flex items-center gap-1 disabled:opacity-40"
                >
                  <Plus size={11} weight="bold" /> Add variant
                </button>
              </div>
              <div className="space-y-3">
                {form.variants.map((v, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-white/10 bg-white/[0.02] p-3"
                    data-testid={`experiment-variant-${idx}`}
                  >
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-3">
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">ID</div>
                        <input
                          type="text"
                          value={v.id}
                          onChange={(e) => setVariant(idx, "id", e.target.value.toLowerCase().replace(/[^a-z0-9_\-]/g, ""))}
                          className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-xs text-white font-mono"
                        />
                      </div>
                      <div className="col-span-5">
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Name</div>
                        <input
                          type="text"
                          value={v.name}
                          onChange={(e) => setVariant(idx, "name", e.target.value)}
                          className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Weight</div>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={v.weight}
                          onChange={(e) => setVariant(idx, "weight", Number(e.target.value) || 1)}
                          className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                        />
                      </div>
                      <div className="col-span-2 flex items-end">
                        {form.variants.length > 2 && (
                          <button
                            onClick={() => removeVariant(idx)}
                            className="w-full text-[10px] uppercase tracking-widest px-2 py-1 rounded-md border border-white/10 text-neutral-500 hover:text-rose-300 hover:border-rose-500/40 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">Content</div>
                      <textarea
                        rows={2}
                        value={v.content}
                        onChange={(e) => setVariant(idx, "content", e.target.value)}
                        placeholder="Copy that will render for this variant"
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="text-sm text-neutral-500 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="experiment-save-btn"
              className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Flask size={13} weight="fill" /> {saving ? "Saving..." : (isEdit ? "Save changes" : "Create experiment")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsPanel({ expId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await client.get(`/admin/experiments/${expId}/analytics`);
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load analytics");
      } finally { setLoading(false); }
    })();
  }, [expId]);

  const perVariant = Object.values(data?.per_variant || {});
  const bestRate = Math.max(...perVariant.map((r) => r.conversion_rate_pct || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="experiment-analytics-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-3xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                <ChartBar size={12} weight="fill" /> Experiment analytics
              </div>
              <div className="text-lg font-semibold text-white">
                {data?.experiment?.name || "Loading…"}
              </div>
              {data?.experiment && (
                <div className="text-xs text-neutral-500 mt-0.5 font-mono">
                  {data.experiment.key} · {data.experiment.page_path} · slot: {data.experiment.slot_key}
                </div>
              )}
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white"
                    data-testid="experiment-analytics-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="py-8 text-center text-neutral-500">Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="card-surface p-4">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Assignments</div>
                    <div className="text-3xl font-display font-semibold mt-1" data-testid="analytics-total-assignments">
                      {data?.total_assignments || 0}
                    </div>
                  </div>
                  <div className="card-surface p-4">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Signups</div>
                    <div className="text-3xl font-display font-semibold mt-1 text-[#FF5A1F]"
                         data-testid="analytics-total-conversions">
                      {data?.total_conversions || 0}
                    </div>
                  </div>
                  <div className="card-surface p-4">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Overall rate</div>
                    <div className="text-3xl font-display font-semibold mt-1 text-emerald-400">
                      {data?.overall_conversion_rate_pct || 0}%
                    </div>
                  </div>
                </div>

                <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-3">Per variant</div>
                <div className="space-y-2">
                  {perVariant.length === 0 ? (
                    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-neutral-500">
                      No data yet — start driving traffic to <span className="text-white font-mono">{data?.experiment?.page_path}</span>.
                    </div>
                  ) : perVariant.map((v) => {
                    const isBest = v.conversion_rate_pct === bestRate && bestRate > 0;
                    const isWinner = v.is_winner === true;
                    return (
                      <div
                        key={v.variant_id}
                        data-testid={`analytics-variant-${v.variant_id}`}
                        className={`rounded-lg border p-3 flex items-center gap-3 ${
                          isWinner
                            ? "border-emerald-400 bg-emerald-500/10 ring-2 ring-emerald-500/30"
                            : isBest
                            ? "border-emerald-500/40 bg-emerald-500/5"
                            : "border-white/10 bg-white/[0.02]"
                        }`}
                      >
                        <div className="w-32 min-w-0">
                          <div className="text-sm text-white font-medium truncate flex items-center gap-1.5">
                            {v.name}
                            {isWinner && (
                              <span
                                className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500 text-black font-bold"
                                data-testid={`analytics-winner-badge-${v.variant_id}`}
                                title={`${v.prob_beats_control_pct}% posterior confidence — ship this`}
                              >
                                WINNER
                              </span>
                            )}
                            {!isWinner && isBest && <TrendUp size={11} weight="fill" className="text-emerald-400" />}
                          </div>
                          <div className="text-[10px] font-mono text-neutral-600 truncate">{v.variant_id}</div>
                        </div>
                        <div className="flex-1 h-2 rounded-full bg-white/[0.04] overflow-hidden">
                          <div
                            className={`h-full ${isWinner ? "bg-emerald-400" : isBest ? "bg-emerald-500/60" : "bg-[#FF5A1F]"}`}
                            style={{ width: `${Math.max(v.conversion_rate_pct || 0, v.assignments > 0 ? 2 : 0)}%` }}
                          />
                        </div>
                        <div className="w-52 text-right text-xs">
                          <div className="text-neutral-300">
                            {v.conversions} / {v.assignments} · <span className="text-white font-semibold">{v.conversion_rate_pct}%</span>
                          </div>
                          {v.lift_vs_control_pct != null && v.variant_id !== perVariant[0].variant_id && (
                            <div className={`text-[11px] mt-0.5 ${v.lift_vs_control_pct >= 0 ? "text-emerald-400" : "text-rose-300"}`}>
                              {v.lift_vs_control_pct >= 0 ? "+" : ""}{v.lift_vs_control_pct}% vs control
                            </div>
                          )}
                          {v.prob_beats_control_pct != null && (
                            <div className="text-[10px] text-neutral-500 mt-0.5">
                              {v.prob_beats_control_pct}% likely to beat control
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Sample-size warning if any non-control variant is under min_sample */}
                {data && perVariant.length > 1 && perVariant.slice(1).some((v) => v.assignments < (data.stat_sig_min_sample || 100)) && (
                  <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold pt-0.5">Heads-up</div>
                    <div className="text-xs text-amber-100 flex-1">
                      Need at least {data.stat_sig_min_sample || 100} visitors per variant to declare a winner
                      (currently at {Math.min(...perVariant.map((v) => v.assignments))}).
                      Winner badges appear at {data.stat_sig_threshold_pct || 95}%+ posterior confidence.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminExperimentsPage() {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formModal, setFormModal] = useState({ open: false, initial: null });
  const [analyticsModal, setAnalyticsModal] = useState({ open: false, id: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/experiments");
      setExperiments(data?.experiments || []);
    } catch (e) {
      toast.error("Could not load experiments");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async (exp) => {
    const next = exp.status === "running" ? "stopped" : "running";
    try {
      await client.patch(`/admin/experiments/${exp.id}`, { status: next });
      toast.success(next === "running" ? "Experiment is now live" : "Experiment stopped");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteExp = async (exp) => {
    if (!window.confirm(`Delete '${exp.name}' and all its assignment data? This cannot be undone.`)) return;
    try {
      await client.delete(`/admin/experiments/${exp.id}`);
      toast.success("Experiment deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key).then(() => toast.success("Key copied"));
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="experiments-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
            <Flask size={12} weight="fill" /> Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">A/B Experiments</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Split-test headlines, CTAs, and hero copy on landing pages. Every visitor gets a
            stable variant via cookie; signup completions flow back as conversions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="experiments-refresh-btn"
            className="rounded-full px-4 py-2 text-sm border border-white/10 hover:border-white/25 text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-2 disabled:opacity-60"
          >
            <ArrowClockwise size={13} /> Refresh
          </button>
          <button
            onClick={() => setFormModal({ open: true, initial: null })}
            data-testid="experiments-create-btn"
            className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
          >
            <Plus size={13} weight="bold" /> New experiment
          </button>
        </div>
      </div>

      <div className="card-surface overflow-hidden" data-testid="experiments-list">
        {experiments.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">
            <Flask size={40} className="mx-auto text-[#FF5A1F] mb-3" />
            <div className="text-lg text-white font-semibold">No experiments yet</div>
            <div className="text-sm mt-1">Create your first A/B test to start optimizing your landing pages.</div>
            <button
              onClick={() => setFormModal({ open: true, initial: null })}
              className="mt-4 btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
            >
              <Plus size={13} weight="bold" /> Create your first experiment
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-neutral-500">
                <th className="text-left px-4 py-3">Experiment</th>
                <th className="text-left px-4 py-3">Path / Slot</th>
                <th className="text-left px-4 py-3">Variants</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {experiments.map((exp) => (
                <tr
                  key={exp.id}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                  data-testid={`experiment-row-${exp.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-white">{exp.name}</div>
                    <button
                      onClick={() => copyKey(exp.key)}
                      className="text-[11px] text-neutral-500 hover:text-neutral-300 font-mono inline-flex items-center gap-1 mt-0.5"
                      title="Copy key"
                    >
                      {exp.key} <Copy size={9} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-neutral-300 font-mono">{exp.page_path}</div>
                    <div className="text-[11px] text-neutral-500 font-mono mt-0.5">slot: {exp.slot_key}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-neutral-300">{exp.variants?.length || 0} variants</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {(exp.variants || []).map((v) => v.name).join(" · ")}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={exp.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => setAnalyticsModal({ open: true, id: exp.id })}
                        data-testid={`experiment-analytics-btn-${exp.id}`}
                        title="View analytics"
                        className="text-xs rounded-full px-2.5 py-1.5 border border-white/10 text-neutral-300 hover:text-white hover:border-white/25 transition-colors inline-flex items-center gap-1"
                      >
                        <ChartBar size={11} /> Stats
                      </button>
                      <button
                        onClick={() => toggleStatus(exp)}
                        data-testid={`experiment-toggle-btn-${exp.id}`}
                        title={exp.status === "running" ? "Stop experiment" : "Start experiment"}
                        className={`text-xs rounded-full px-2.5 py-1.5 border transition-colors inline-flex items-center gap-1 ${
                          exp.status === "running"
                            ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                            : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        }`}
                      >
                        {exp.status === "running"
                          ? <><Stop size={11} weight="fill" /> Stop</>
                          : <><Play size={11} weight="fill" /> Start</>}
                      </button>
                      <button
                        onClick={() => setFormModal({ open: true, initial: exp })}
                        data-testid={`experiment-edit-btn-${exp.id}`}
                        title="Edit"
                        className="text-xs rounded-full px-2.5 py-1.5 border border-white/10 text-neutral-500 hover:text-white hover:border-white/25 transition-colors"
                      >
                        <PencilSimple size={11} />
                      </button>
                      <button
                        onClick={() => deleteExp(exp)}
                        data-testid={`experiment-delete-btn-${exp.id}`}
                        title="Delete"
                        className="text-xs rounded-full px-2.5 py-1.5 border border-white/10 text-neutral-500 hover:text-rose-300 hover:border-rose-500/40 transition-colors"
                      >
                        <Trash size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ExperimentForm
        open={formModal.open}
        initial={formModal.initial}
        onClose={() => setFormModal({ open: false, initial: null })}
        onSaved={load}
      />

      {analyticsModal.open && (
        <AnalyticsPanel
          expId={analyticsModal.id}
          onClose={() => setAnalyticsModal({ open: false, id: null })}
        />
      )}
    </div>
  );
}
